"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, MessageCircle, Loader, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="58611" className="bg-accent/20 border border-border p-8 text-center animate-in fade-in zoom-in duration-300 rounded-xl">
        <div className="flex justify-center mb-4">
          <div className="bg-primary text-primary-foreground p-3 rounded-full">
            <Send className="size-6" />
          </div>
        </div>
        <h3 className="text-xl font-bold text-primary mb-2">Inquiry Received!</h3>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-xl border-primary text-primary hover:bg-primary hover:text-primary-foreground w-full sm:w-auto transition-colors">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="name" rules={{
      required: "Full name is required",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary font-semibold tracking-tight">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="Your Name" {...field} className="rounded-xl border-input bg-background/50 focus-visible:ring-primary/20 h-12" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-semibold tracking-tight">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="hello@example.com" {...field} className="rounded-xl border-input bg-background/50 focus-visible:ring-primary/20 h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary font-semibold tracking-tight">WhatsApp Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+62 ..." {...field} className="rounded-xl border-input bg-background/50 focus-visible:ring-primary/20 h-12" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="subject" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary font-semibold tracking-tight">Event Type <span className="text-muted-foreground text-xs font-normal">(Optional)</span></FormLabel>
            <FormControl>
              <Input placeholder="e.g. Private Dining, Wedding, Workshop" {...field} className="rounded-xl border-input bg-background/50 focus-visible:ring-primary/20 h-12" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please tell us about your event",
      minLength: {
        value: 10,
        message: "Please provide more details"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary font-semibold tracking-tight">Tell us about your event</FormLabel>
            <FormControl>
              <Textarea placeholder="Date, number of guests, and any special requests..." {...field} className="rounded-xl border-input bg-background/50 focus-visible:ring-primary/20 min-h-[150px] resize-y" />
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground italic">
              Help us prepare the perfect package for you.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-xl">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 text-base font-bold uppercase tracking-widest transition-all shadow-md hover:shadow-lg">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Checking...
          </> : "Check Availability"}
      </Button>
    </Form>;
}
export default function ContactUsDirectly() {
  return <section id="start-planning-your-event" className="bg-background py-16 md:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          
          {}
          <div className="order-1 lg:order-1">
            <div className="mb-8">
              <h2 className="text-3xl md:text-5xl font-bold text-primary mb-4 tracking-tighter font-serif">Start Planning Your Event</h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Connect with our team to check availability and receive a customized event package for your gathering in Ubud.
              </p>
            </div>

            <FormProvider formKey="event_planning_form_home" formId="event_planning_form_home" sectionName="start-planning-your-event" defaultValues={{
            name: "",
            email: "",
            phone: "",
            subject: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Check Availability" successMessage="Thank you! We've received your inquiry and will reach out shortly to discuss your Ubud event." errorMessage="Something went wrong. Please try again or contact us via WhatsApp." className="w-full">
              <ContactFormContent />
            </FormProvider>
          </div>

          {}
          <div className="order-2 lg:order-2 flex flex-col gap-10 lg:pt-20">
            
            {}
            <div className="bg-accent/20 p-8 border border-border/50 rounded-xl backdrop-blur-sm">
              <h3 className="text-xl font-bold text-primary mb-4 font-serif">Prefer Instant Chat?</h3>
              <p className="text-muted-foreground mb-6">
                Message us directly on WhatsApp for a quick response regarding venue tours and immediate booking inquiries.
              </p>
              <Button asChild className="w-full h-14 rounded-xl bg-[#25D366] hover:bg-[#20bd5a] text-white text-lg font-bold shadow-sm transition-all hover:shadow-md">
                <Link to="https://wa.me/628123456789" newTab>
                  <MessageCircle className="mr-2 size-6 fill-current" />
                  Chat via WhatsApp
                </Link>
              </Button>
            </div>

            {}
            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="bg-secondary/10 p-3 rounded-xl shrink-0">
                  <MapPin className="size-6 text-secondary" />
                </div>
                <div>
                  <h4 className="font-bold text-primary text-lg mb-1 font-serif">Our Location</h4>
                  <p className="text-muted-foreground">Peliatan, Ubud, Gianyar, Bali 80571</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-secondary/10 p-3 rounded-xl shrink-0">
                  <Phone className="size-6 text-secondary" />
                </div>
                <div>
                  <h4 className="font-bold text-primary text-lg mb-1 font-serif">Call Us</h4>
                  <p className="text-muted-foreground" dir="ltr">+62 812 3456 789</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-secondary/10 p-3 rounded-xl shrink-0">
                  <Mail className="size-6 text-secondary" />
                </div>
                <div>
                  <h4 className="font-bold text-primary text-lg mb-1 font-serif">Email Us</h4>
                  <p className="text-muted-foreground font-sans">events@ubudspace.com</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-secondary/10 p-3 rounded-xl shrink-0">
                  <Clock className="size-6 text-secondary" />
                </div>
                <div>
                  <h4 className="font-bold text-primary text-lg mb-1 font-serif">Visiting Hours</h4>
                  <p className="text-muted-foreground">Daily: 9:00 AM – 6:00 PM</p>
                  <p className="text-muted-foreground italic text-sm">Tours by appointment only</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>;
}